import { motion } from "framer-motion";
import { School, Building2, PartyPopper, Users, HeartHandshake, Sparkles, ArrowRight, Mail, Phone } from "lucide-react";
import { SITE } from "../data/site";

const AUDIENCES = [
  { icon: School, title: "Schools", desc: "Curriculum workshops, assemblies and after-school clubs." },
  { icon: Building2, title: "Organisations", desc: "Corporate sessions, away days and wellbeing programmes." },
  { icon: PartyPopper, title: "Festivals", desc: "Performance bookings, stage takeovers and audience workshops." },
  { icon: Users, title: "Youth Programmes", desc: "Borough councils, charities and outreach initiatives." },
  { icon: HeartHandshake, title: "Team Building", desc: "Energetic, choreography-led group experiences." },
  { icon: Sparkles, title: "Bespoke Creative Workshops", desc: "Tailored sessions built around your brief and audience." },
];

const PROCESS = [
  { step: "01", title: "Enquire", desc: "Send us your brief — audience, dates, location." },
  { step: "02", title: "Tailor", desc: "We design the workshop around your goals." },
  { step: "03", title: "Deliver", desc: "Our team turns up ready to move your people." },
];

export const TrainingOnRequest = () => {
  return (
    <section
      id="bookings"
      data-testid="bookings-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-gradient-to-b from-black via-zinc-950 to-black"
    >
      <div className="absolute -top-32 left-1/4 w-[500px] h-[500px] rounded-full bg-brand-purple/20 blur-[160px]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-brand-yellow/8 blur-[150px]" />
      <div className="absolute inset-0 noise opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">10 · Training On Request</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide leading-[0.95]">
              Book USDD for <br />
              <span className="text-brand-yellow text-glow-yellow">your team</span>, school
              <br /> or <span className="text-brand-purpleLight">festival</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-zinc-300/90 text-base sm:text-lg leading-relaxed">
              From one-off masterclasses to multi-week residencies — we design
              and deliver bespoke training across the UK and beyond. One brief,
              one team, one unforgettable session.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#contact"
                data-testid="bookings-enquire-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.03] transition-all glow-yellow"
              >
                Enquire now <ArrowRight size={14} />
              </a>
              <a
                href={`mailto:${SITE.contact.email}?subject=USDD%20Workshop%20Booking%20Enquiry`}
                data-testid="bookings-email-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white font-bold uppercase text-xs tracking-[0.2em] hover:bg-white hover:text-black transition-all"
              >
                <Mail size={14} /> Email
              </a>
            </div>
          </div>
        </div>

        {/* Audience grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {AUDIENCES.map((a, i) => (
            <motion.a
              key={a.title}
              href="#contact"
              data-testid={`booking-audience-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="group relative p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-brand-yellow/50 hover:bg-white/[0.06] hover:-translate-y-1 transition-all"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="w-11 h-11 rounded-xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center group-hover:bg-brand-yellow transition-all">
                  <a.icon size={20} className="text-brand-yellow group-hover:text-black transition-colors" />
                </div>
                <ArrowRight className="text-zinc-600 group-hover:text-brand-yellow group-hover:translate-x-1 transition-all" size={18} />
              </div>
              <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{a.title}</h3>
              <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{a.desc}</p>
            </motion.a>
          ))}
        </div>

        {/* Process strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10 mb-12">
          {PROCESS.map((p) => (
            <div key={p.step} className="bg-black/80 p-7">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-5xl text-brand-yellow tracking-wide">{p.step}</span>
                <div>
                  <div className="font-display text-2xl text-white tracking-wide">{p.title}</div>
                  <div className="text-xs text-zinc-400 mt-1 max-w-xs">{p.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Big enquiry CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep via-black to-black p-8 sm:p-12"
        >
          <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-brand-yellow/15 blur-[100px]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-3">Ready when you are</div>
              <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-wide leading-[0.95]">
                Let's build something
                <br />
                <span className="text-brand-yellow text-glow-yellow">unforgettable</span>.
              </h3>
              <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-xl">
                Tell us about your audience, dates and goals — we'll come back
                with a tailored proposal within 48 hours.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-3">
              <a
                href="#contact"
                data-testid="bookings-request-quote-btn"
                className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
              >
                Request a quote
                <ArrowRight size={16} />
              </a>
              <a
                href={`tel:${SITE.contact.phone.replace(/\s/g, "")}`}
                data-testid="bookings-call-btn"
                className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black hover:border-white transition-all"
              >
                <span className="inline-flex items-center gap-2"><Phone size={15} /> Call us</span>
                <span className="text-xs opacity-70">{SITE.contact.phone}</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
